// UC-9 (plan.md §4, decisión E de tasks.md). `onSaved` es el `refresh` de `useClientCompanies`,
// cableado por el contenedor (`ClientCompaniesTab`) — sin acoplar este hook a ese otro. Mensajes
// literales españoles as-is.

import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { clientCompanyRepository } from '../infrastructure/clientCompany.repository';
import { toClientCompanyFormValues, validateClientCompany } from '../domain/clientCompany.rules';
import { apiErrorMessage } from './apiError';
import type { Id } from '../domain/enterprise.types';
import type { ClientCompany, ClientCompanyFormValues } from '../domain/clientCompany.types';

const EMPTY_FORM: ClientCompanyFormValues = {
  name: '',
  nit: '',
  description: '',
  status: 'active',
};

export function useClientCompanyForm(clientId: Id, onSaved: () => void) {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ClientCompany | null>(null);
  const [form, setForm] = useState<ClientCompanyFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  }

  function openEdit(company: ClientCompany) {
    setEditing(company);
    setForm(toClientCompanyFormValues(company));
    setOpen(true);
  }

  function close() {
    setOpen(false);
  }

  function setField<K extends keyof ClientCompanyFormValues>(
    field: K,
    value: ClientCompanyFormValues[K]
  ) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function save() {
    const validation = validateClientCompany(form);
    if (!validation.ok) {
      enqueueSnackbar('El nombre es requerido', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await clientCompanyRepository.update(clientId, editing.id, { ...form });
        enqueueSnackbar('Empresa actualizada', { variant: 'success' });
      } else {
        await clientCompanyRepository.create(clientId, { ...form });
        enqueueSnackbar('Empresa creada', { variant: 'success' });
      }
      setOpen(false);
      onSaved();
    } catch (err) {
      enqueueSnackbar(apiErrorMessage(err) || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  return { open, editing, form, setField, saving, openCreate, openEdit, close, save };
}
